import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Card,
  CircularProgress,
  Backdrop,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Switch,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  Storefront,
  Schedule,
  Groups,
  RestaurantMenu,
  Bolt,
  DeleteOutline,
  CalendarToday,
  Hub,
  RocketLaunch,
  AutoAwesome,
  VerifiedUser,
  Security,
  PlayCircle,
  OpenInNew,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

/* ===================== CONSTANTS ===================== */
// Square client ID now comes from environment so we can switch
// between sandbox and production without code changes.
const SQUARE_CLIENT_ID = process.env.REACT_APP_SQUARE_CLIENT_ID || '';
// Detect sandbox vs production based on the client ID prefix.
const SQUARE_IS_SANDBOX = SQUARE_CLIENT_ID.startsWith('sandbox-');

const steps = [
  { title: 'Site & Staff', icon: Storefront },
  { title: 'Menu & POS', icon: RestaurantMenu },
  { title: 'Utilities & Waste', icon: Bolt },
  { title: 'Events & Social', icon: Hub },
];


const Onboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const { register, handleSubmit, formState: { errors }, watch, trigger, setValue } = useForm();
  const navigate = useNavigate();
  const { user, setCurrentSiteId } = useAuth();

  const [staffMembers, setStaffMembers] = useState([{ id: 1, firstName: 'John', lastName: 'Doe', role: 'Head Chef', hourlyWage: 18.50, permissions: ['viewSales', 'editRoster', 'approveStock'] }]);
  // Editable menu items configured during onboarding (for when POS menu is missing or needs tweaks)
  const [menuItems, setMenuItems] = useState([
    { id: 1, name: '', category: '', price: '', recipe: '' },
  ]);
  const [events, setEvents] = useState([
    { id: 1, name: 'Main St. Farmers Market', frequency: 'Weekly', impact: 'High' },
    { id: 2, name: 'Annual Music Festival', frequency: 'Annual', impact: 'Extreme' },
  ]);
  const [connectedPOS, setConnectedPOS] = useState(null);
  const [connectingPOS, setConnectingPOS] = useState(null);
  // Hold the latest POS OAuth tokens temporarily during onboarding
  const [posTokens, setPosTokens] = useState(null);
  const [loadingSquareMenu, setLoadingSquareMenu] = useState(false);
  const [, setSquareMenuError] = useState('');
  const [operatingHours, setOperatingHours] = useState({
    monday: { open: '09:00', close: '22:00', closed: false },
    tuesday: { open: '09:00', close: '22:00', closed: false },
    wednesday: { open: '09:00', close: '22:00', closed: false },
    thursday: { open: '09:00', close: '22:00', closed: false },
    friday: { open: '09:00', close: '23:30', closed: false },
    saturday: { open: '09:00', close: '23:30', closed: false },
    sunday: { open: '10:00', close: '21:00', closed: false },
  });
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    role: '',
    hourlyWage: '',
    postcode: '',
    permissions: []
  });

  const handleNext = async () => {
    setLoading(true);
    
    const stepFields = getStepFields(activeStep);
    const isValid = await trigger(stepFields);
    
    if (!isValid) {
      setLoading(false);
      return;
    }

    if (!completedSteps.includes(activeStep)) {
      setCompletedSteps([...completedSteps, activeStep]);
    }

    setTimeout(() => {
      if (activeStep === steps.length - 1) {
        // Final step: complete onboarding via API
        completeOnboarding();
      } else {
        setActiveStep((prevStep) => prevStep + 1);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 500);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStepFields = (step) => {
    const fieldMap = {
      // Step 0 – core site profile
      0: ['restaurantName', 'address', 'postcode', 'serviceType', 'floorAreaM2'],
      // Step 1 – POS is optional; no hard required fields
      1: [],
      // Step 2 – only require energy ingestion mode for now
      2: ['energyIngestionMode'],
      3: [],
    };
    return fieldMap[step] || [];
  };

  const addStaffMember = () => {
    if (!newMember.firstName || !newMember.lastName) {
      return;
    }
    const member = {
      id: Date.now(),
      firstName: newMember.firstName,
      lastName: newMember.lastName,
      role: newMember.role || 'Staff',
      hourlyWage: parseFloat(newMember.hourlyWage) || 0,
      postcode: newMember.postcode,
      permissions: newMember.permissions
    };
    setStaffMembers([...staffMembers, member]);
    setNewMember({
      firstName: '',
      lastName: '',
      role: '',
      hourlyWage: '',
      postcode: '',
      permissions: []
    });
  };

  const togglePermission = (permission) => {
    setNewMember(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const removeStaffMember = (id) => {
    if (staffMembers.length > 1) {
      setStaffMembers(staffMembers.filter(member => member.id !== id));
    }
  };

  const addEvent = () => {
    setEvents([...events, { id: Date.now(), name: '', frequency: 'Once', impact: 'Medium' }]);
  };

  const addMenuItemRow = () => {
    setMenuItems((prev) => [
      ...prev,
      { id: Date.now(), name: '', category: '', price: '', recipe: '' },
    ]);
  };

  const removeMenuItemRow = (id) => {
    setMenuItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateMenuItemField = (id, field, value) => {
    setMenuItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handlePOSConnection = (posProvider) => {
    setConnectingPOS(posProvider);
    
    // Generate OAuth URL based on provider
    // Redirect URI must match what's configured in Square Developer Portal
    // Default: /dashboard/pos/callback (where our callback handler is located)
    const redirectUri = process.env.REACT_APP_SQUARE_REDIRECT_URI || `${window.location.origin}/dashboard/pos/callback`;
    const state = JSON.stringify({ provider: posProvider, source: 'onboarding' });
    
    // Choose correct Square base URL for sandbox vs production.
    const squareBaseUrl = SQUARE_IS_SANDBOX
      ? 'https://connect.squareupsandbox.com'
      : 'https://connect.squareup.com';

    const oauthUrls = {
      'Square': (() => {
        const scopes = encodeURIComponent('PAYMENTS_READ ORDERS_READ ITEMS_READ INVENTORY_READ MERCHANT_PROFILE_READ');
        return `${squareBaseUrl}/oauth2/authorize` +
          `?client_id=${encodeURIComponent(SQUARE_CLIENT_ID)}` +
          `&scope=${scopes}` +
          `&session=false` +
          `&response_type=code` +
          `&state=${encodeURIComponent(state)}` +
          `&redirect_uri=${encodeURIComponent(redirectUri)}`;
      })(),
      'Lightspeed': `https://cloud.lightspeedapp.com/oauth/authorize.php?client_id=${process.env.REACT_APP_LIGHTSPEED_CLIENT_ID || 'YOUR_LIGHTSPEED_CLIENT_ID'}&scope=employee:all&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state)}`,
      'Zettle': `https://oauth.zettle.com/authorize?client_id=${process.env.REACT_APP_ZETTLE_CLIENT_ID || 'YOUR_ZETTLE_CLIENT_ID'}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=READ:PURCHASE&state=${encodeURIComponent(state)}`,
    };

    const oauthUrl = oauthUrls[posProvider] || `${redirectUri}?provider=${posProvider}&source=onboarding`;
    
    // Open OAuth window
    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    const authWindow = window.open(
      oauthUrl,
      `${posProvider} OAuth`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    // Listen for OAuth callback via postMessage
    const handleMessage = (event) => {
      // Security: Verify the message is from our own origin
      if (event.origin !== window.location.origin) {
        console.warn('Ignoring postMessage from unauthorized origin:', event.origin);
        return;
      }

      if (event.data && event.data.type === 'POS_OAUTH_SUCCESS' && event.data.provider === posProvider) {
        // OAuth code received - in production, send this to your backend to exchange for access token
        const authCode = event.data.code;
        console.log('[Onboarding] POS OAuth code received in parent window:', {
          provider: posProvider,
          authCode,
        });

        // Exchange code for tokens via backend so they can be stored.
        (async () => {
          try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
            const res = await fetch(`${baseUrl}/api/pos/oauth/exchange`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                code: authCode,
                provider: posProvider,
                // Pass through the exact redirect URI we used in the authorize step
                redirectUri,
              }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.success) {
              console.error('[Onboarding] Failed to exchange POS OAuth code:', body);
            } else {
              console.log('[Onboarding] POS tokens received from backend:', body.tokens);
              // Temporarily hold tokens in state for this onboarding session.
              setPosTokens(body.tokens || null);
            }
          } catch (err) {
            console.error('[Onboarding] Error exchanging POS OAuth code:', err);
          } finally {
            setConnectedPOS(posProvider);
            setConnectingPOS(null);
            authWindow?.close();
            window.removeEventListener('message', handleMessage);
          }
        })();
      } else if (event.data && event.data.type === 'POS_OAUTH_ERROR' && event.data.provider === posProvider) {
        setConnectingPOS(null);
        authWindow?.close();
        window.removeEventListener('message', handleMessage);
        alert(`Connection failed: ${event.data.error || 'Please try again.'}`);
      }
    };

    window.addEventListener('message', handleMessage);

    // Fallback: Check if window was closed manually
    const checkClosed = setInterval(() => {
      if (authWindow?.closed) {
        clearInterval(checkClosed);
        setConnectingPOS(null);
        window.removeEventListener('message', handleMessage);
      }
    }, 500);

    // Cleanup after 10 minutes
    setTimeout(() => {
      clearInterval(checkClosed);
      if (authWindow && !authWindow.closed) {
        authWindow.close();
      }
      setConnectingPOS(null);
      window.removeEventListener('message', handleMessage);
    }, 600000);
  };

  const loadSquareMenuPreview = async () => {
    if (!posTokens?.access_token) {
      return;
    }
    setLoadingSquareMenu(true);
    setSquareMenuError('');
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
      const res = await fetch(`${baseUrl}/api/pos/square/menu-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: posTokens.access_token,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) {
        throw new Error(body.message || 'Failed to load menu from Square');
      }
      const imported = (body.items || []).map((item, idx) => ({
        id: Date.now() + idx,
        name: item.name || '',
        category: item.category || '',
        // price from Square is in major units already from backend
        price: item.price != null ? String(item.price) : '',
        recipe: '',
      }));
      if (imported.length) {
        setMenuItems(imported);
      }
    } catch (err) {
      console.error('[Onboarding] Error loading Square menu preview:', err);
      setSquareMenuError(err.message || 'Failed to load menu from Square');
    } finally {
      setLoadingSquareMenu(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
              {/* Left Column - Restaurant Info & Hours */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Restaurant Profile */}
                <Card sx={{ p: 4, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 3, mb: 4 }}>
                    <Box sx={{ 
                      width: 56, 
                      height: 56, 
                      borderRadius: 2, 
                      bgcolor: 'primary.light', 
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Storefront sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Your Restaurant Profile
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Details about your physical location.
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Restaurant Name"
                        {...register('restaurantName', { required: 'Restaurant name is required' })}
                        error={!!errors.restaurantName}
                        helperText={errors.restaurantName?.message}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required error={!!errors.serviceType}>
                        <InputLabel id="business-type-label" sx={{ backgroundColor: 'background.paper', px: 1 }}>Business Type</InputLabel>
                        <Select
                          labelId="business-type-label"
                          label="Business Type"
                          value={watch('serviceType') || ''}
                          {...register('serviceType', { required: 'Service type is required' })}
                          displayEmpty
                          sx={{ 
                            borderRadius: 2,
                            minWidth: '100%',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: errors.serviceType ? 'error.main' : undefined
                            }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 200,
                                maxHeight: 300,
                                '& .MuiMenuItem-root': {
                                  minHeight: 48
                                }
                              }
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select business type</em>
                          </MenuItem>
                          <MenuItem value="QSR">Quick Service Restaurant</MenuItem>
                          <MenuItem value="Fine Dining">Fine Dining</MenuItem>
                          <MenuItem value="Takeaway">Takeaway</MenuItem>
                          <MenuItem value="Cafe">Cafe</MenuItem>
                          <MenuItem value="Bar">Bar</MenuItem>
                          <MenuItem value="Food Truck">Food Truck</MenuItem>
                        </Select>
                        {errors.serviceType && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {errors.serviceType.message}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        required
                        fullWidth
                        label="Postcode"
                        {...register('postcode', { required: 'Postcode is required' })}
                        error={!!errors.postcode}
                        helperText={errors.postcode?.message}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        required
                        fullWidth
                        label="Full Address"
                        {...register('address', { required: 'Address is required' })}
                        error={!!errors.address}
                        helperText={errors.address?.message}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Floor Area (m²)"
                        {...register('floorAreaM2')}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                </Card>

                {/* Operating Hours */}
                <Card sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 3, mb: 4 }}>
                    <Box sx={{ 
                      width: 56, 
                      height: 56, 
                      borderRadius: 2, 
                      bgcolor: 'primary.light', 
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Schedule sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Operational Hours
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Standard opening and closing times.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                      const dayKey = day.toLowerCase();
                      const dayData = operatingHours[dayKey];
                      const isClosed = dayData?.closed || false;
                      
                      return (
                        <Box
                          key={day}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: isClosed ? 'grey.50' : 'transparent',
                            opacity: isClosed ? 0.6 : 1,
                            transition: 'all 0.2s',
                            border: '1px solid',
                            borderColor: isClosed ? 'grey.200' : 'grey.100'
                          }}
                        >
                          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                            <Grid item xs={4}>
                              <Typography 
                                variant="body2" 
                                fontWeight="bold"
                                sx={{ color: isClosed ? 'text.disabled' : 'text.primary' }}
                              >
                                {day}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                fullWidth
                                type="time"
                                value={dayData?.open || '09:00'}
                                disabled={isClosed}
                                {...register(`${dayKey}Open`)}
                                onChange={(e) => {
                                  setOperatingHours(prev => ({
                                    ...prev,
                                    [dayKey]: { ...prev[dayKey], open: e.target.value }
                                  }));
                                }}
                                size="small"
                                sx={{ 
                                  '& .MuiOutlinedInput-root': { 
                                    borderRadius: 2,
                                    '&.Mui-disabled': {
                                      bgcolor: 'grey.100',
                                      '& fieldset': {
                                        borderColor: 'grey.300'
                                      }
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                fullWidth
                                type="time"
                                value={dayData?.close || '22:00'}
                                disabled={isClosed}
                                {...register(`${dayKey}Close`)}
                                onChange={(e) => {
                                  setOperatingHours(prev => ({
                                    ...prev,
                                    [dayKey]: { ...prev[dayKey], close: e.target.value }
                                  }));
                                }}
                                size="small"
                                sx={{ 
                                  '& .MuiOutlinedInput-root': { 
                                    borderRadius: 2,
                                    '&.Mui-disabled': {
                                      bgcolor: 'grey.100',
                                      '& fieldset': {
                                        borderColor: 'grey.300'
                                      }
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <FormControlLabel
                                control={
                                  <Switch 
                                    checked={!isClosed}
                                    {...register(`${dayKey}Closed`)}
                                    onChange={(e) => {
                                      const newClosed = !e.target.checked;
                                      setOperatingHours(prev => ({
                                        ...prev,
                                        [dayKey]: { ...prev[dayKey], closed: newClosed }
                                      }));
                                    }}
                                  />
                                }
                                label=""
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              </Box>

              {/* Right Column - Team/Staff Management */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Card sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 3, mb: 4 }}>
                    <Box sx={{ 
                      width: 56, 
                      height: 56, 
                      borderRadius: 2, 
                      bgcolor: 'primary.light', 
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Groups sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        The Heart of Your Team
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add staff members to manage schedules and pay rates.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Staff Table */}
                  <Box sx={{ mb: 4, flex: 1 }}>
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: (theme) =>
                              theme.palette.mode === 'dark'
                                ? theme.palette.background.paper
                                : 'grey.50',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1, py: 2 }}>Team Member</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1, py: 2 }}>Role</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1, py: 2 }}>Pay Rate</TableCell>
                          <TableCell align="right" sx={{ py: 2 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staffMembers.map((member, index) => (
                          <TableRow key={member.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                  width: 40, 
                                  height: 40, 
                                  borderRadius: '50%', 
                                  bgcolor: 'primary.light', 
                                  color: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem'
                                }}>
                                  {member.firstName?.[0]}{member.lastName?.[0]}
                                </Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {member.firstName} {member.lastName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={member.role || 'Staff'} 
                                size="small" 
                                sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 'bold' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">£{member.hourlyWage || '0.00'}/hr</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton 
                                size="small" 
                                onClick={() => removeStaffMember(member.id)}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'error.light' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  {/* Add New Member Form */}
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.background.paper
                          : 'grey.50',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: 2, 
                        bgcolor: 'white',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main'
                      }}>
                        <AddIcon />
                      </Box>
                      <Typography variant="h6" fontWeight="bold">
                        Add a New Member
                      </Typography>
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          size="small"
                          value={newMember.firstName}
                          onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          size="small"
                          value={newMember.lastName}
                          onChange={(e) => setNewMember({ ...newMember, lastName: e.target.value })}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Custom Role"
                          size="small"
                          value={newMember.role}
                          onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                          placeholder="e.g. Sous Chef"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Hourly Wage (£)"
                          type="number"
                          size="small"
                          value={newMember.hourlyWage}
                          onChange={(e) => setNewMember({ ...newMember, hourlyWage: e.target.value })}
                          placeholder="12.50"
                          inputProps={{ step: '0.01' }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Home Postcode"
                          size="small"
                          value={newMember.postcode}
                          onChange={(e) => setNewMember({ ...newMember, postcode: e.target.value })}
                          placeholder="e.g. SW1A 1AA"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>
                    
                    {/* Permissions Section */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Assign Permissions
                      </Typography>
                      <Grid container spacing={1.5}>
                        {[
                          { key: 'viewSales', label: 'View Sales' },
                          { key: 'editRoster', label: 'Edit Roster' },
                          { key: 'approveStock', label: 'Approve Stock' },
                          { key: 'manageMenu', label: 'Manage Menu' },
                          { key: 'viewReports', label: 'View Reports' },
                          { key: 'manageStaff', label: 'Manage Staff' }
                        ].map((perm) => (
                          <Grid item xs={6} sm={4} key={perm.key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={newMember.permissions.includes(perm.key)}
                                  onChange={() => togglePermission(perm.key)}
                                  size="small"
                                  sx={{
                                    color: 'primary.main',
                                    '&.Mui-checked': {
                                      color: 'primary.main'
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
                                  {perm.label}
                                </Typography>
                              }
                              sx={{
                                m: 0,
                                p: 1.5,
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: newMember.permissions.includes(perm.key) ? 'primary.main' : 'divider',
                                bgcolor: newMember.permissions.includes(perm.key) ? 'primary.light' : 'transparent',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  bgcolor: 'action.hover'
                                }
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                    
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      fullWidth
                      disabled={!newMember.firstName || !newMember.lastName}
                      sx={{ borderRadius: 2, py: 1.5, fontWeight: 'bold' }}
                      onClick={addStaffMember}
                    >
                      Confirm & Add Member
                    </Button>
                  </Paper>
                </Card>
              </Box>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            {/* POS Integration - At Top */}
            <Card
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? theme.palette.background.paper : 'grey.50',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Connect your POS
            </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sync daily sales data automatically
            </Typography>
            </Box>
                <Chip 
                  icon={<VerifiedUser />}
                  label="Secure Connection"
                  sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 'bold' }}
                />
              </Box>

                    <Grid container spacing={2}>
                      {[
                  { name: 'Square', desc: 'Full inventory & sales sync', color: '#000' },
                  { name: 'Lightspeed', desc: 'Advanced reporting link', color: '#e02020' },
                  { name: 'Zettle', desc: 'Quick mobile sales sync', color: '#000' },
                ].map((pos, idx) => {
                  const isConnected = connectedPOS === pos.name;
                  const isConnecting = connectingPOS === pos.name;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Card 
                        onClick={() => !isConnected && !isConnecting && handlePOSConnection(pos.name)}
                        sx={{ 
                          p: 3, 
                          borderRadius: 2, 
                          border: isConnected ? '2px solid' : '1px solid',
                          borderColor: isConnected ? 'primary.main' : 'divider',
                          bgcolor: isConnected ? 'primary.light' : 'transparent',
                          cursor: isConnecting ? 'wait' : 'pointer',
                          transition: 'all 0.3s',
                          position: 'relative',
                          opacity: isConnecting ? 0.7 : 1,
                          '&:hover': { 
                            borderColor: isConnected ? 'primary.main' : 'primary.main', 
                            boxShadow: 4,
                            transform: isConnecting ? 'none' : 'translateY(-2px)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: 1, 
                            bgcolor: pos.color,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '1.25rem'
                          }}>
                            {pos.name[0]}
                          </Box>
                          {isConnected ? (
                            <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                          ) : isConnecting ? (
                            <CircularProgress size={20} sx={{ color: 'primary.main' }} />
                          ) : (
                            <CheckCircle sx={{ color: 'text.disabled', fontSize: 20 }} />
                          )}
                        </Box>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {pos.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          {pos.desc}
                        </Typography>
                        {isConnected && (
                          <Chip 
                            label="Connected" 
                      size="small"
                            sx={{ 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }} 
                          />
                        )}
                        {isConnecting && (
                          <Chip 
                            label="Connecting..." 
                      size="small"
                            sx={{ 
                              bgcolor: 'grey.300', 
                              color: 'text.primary', 
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }} 
                          />
                        )}
                        {!isConnected && !isConnecting && (
                          <Button
                            variant="contained"
                      size="small"
                      fullWidth
                            startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                            sx={{ 
                              mt: 1.5,
                              borderRadius: 1.5,
                              fontWeight: 'bold',
                              textTransform: 'none',
                              bgcolor: 'primary.main',
                              '&:hover': { bgcolor: 'primary.dark' }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePOSConnection(pos.name);
                            }}
                          >
                            Connect {pos.name}
                          </Button>
                        )}
              </Card>
                    </Grid>
                  );
                })}
                <Grid item xs={12} sm={6} md={4}>
                  <Card 
                    onClick={() => {
                      // For custom API, show a dialog or navigate to manual setup
                      const customUrl = prompt('Enter your POS API endpoint URL:');
                      if (customUrl) {
                        // Handle custom API connection
                        console.log('Custom API URL:', customUrl);
                        alert('Custom API connection will be implemented. For now, please contact support.');
                      }
                    }}
                    sx={{ 
                      p: 3, 
                      borderRadius: 2, 
                      border: '2px dashed', 
                      borderColor: 'divider',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 140,
                      transition: 'all 0.3s',
                      '&:hover': { 
                        borderColor: 'primary.main', 
                        bgcolor: 'action.hover',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <AddIcon sx={{ color: 'text.secondary', mb: 1, fontSize: 32 }} />
                    <Typography variant="body2" fontWeight="bold" color="text.secondary" textAlign="center">
                      Other POS / Custom API
            </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                      Click to configure
            </Typography>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'start', gap: 2 }}>
                <Security sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography variant="caption" color="text.secondary">
                  Your data is encrypted with AES-256 and only used for carbon calculations. We never sell your transaction history.
              </Typography>
          </Box>
            </Card>

            {/* Menu Setup - 2 Column Layout */}
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <RestaurantMenu sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="bold">
                      Menu Ingredient Setup
            </Typography>
                  </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Categorize your inventory to help our AI calculate emission factors for every dish.
            </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { icon: '🍖', title: 'Meat Ingredients', desc: 'Beef, poultry, pork, and seafood categories' },
                    { icon: '🥬', title: 'Vegetable Ingredients', desc: 'Greens, roots, and organic local produce' },
                    { icon: '🥛', title: 'Dairy & Staples', desc: 'Milk, cheese, butter, and dry pantry items' },
                  ].map((item, idx) => (
                    <Card 
                      key={idx}
                      sx={{ 
                        p: 3, 
                        borderRadius: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: 1.5, 
                          bgcolor: 'primary.light',
                          fontSize: 24
                        }}>
                          {item.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                            {item.desc}
                          </Typography>
                        </Box>
                        <ArrowForward sx={{ color: 'text.disabled' }} />
                      </Box>
                    </Card>
                  ))}
                </Box>

              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 4 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    Quick Setup Tips
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <CheckCircle sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Start with your top 10 dishes
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Focus on your bestsellers first for accurate carbon calculations
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <CheckCircle sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Use standard measurements
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Our AI will handle conversions and emission factors automatically
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <CheckCircle sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Update as you go
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          You can refine ingredient details anytime from your dashboard
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>

                {/* Editable menu for when POS menu is missing or needs tweaking */}
                <Card sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        Editable Menu
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Define your key dishes, prices, categories, and recipes.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {connectedPOS === 'Square' && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={loadSquareMenuPreview}
                          disabled={!posTokens?.access_token || loadingSquareMenu}
                          sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        >
                          {loadingSquareMenu ? 'Importing…' : 'Import from POS'}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={addMenuItemRow}
                        startIcon={<AddIcon />}
                        sx={{ borderRadius: 2, fontWeight: 'bold' }}
                      >
                        Add item
                      </Button>
                    </Box>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Price (£)</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {menuItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Dish name"
                              value={item.name}
                              onChange={(e) => updateMenuItemField(item.id, 'name', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Category (e.g. Food/ Beverage)"
                              value={item.category}
                              onChange={(e) => updateMenuItemField(item.id, 'category', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              inputProps={{ step: '0.01' }}
                              placeholder="12.50"
                              value={item.price}
                              onChange={(e) => updateMenuItemField(item.id, 'price', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Optional recipe / key ingredients"
                              value={item.recipe}
                              onChange={(e) => updateMenuItemField(item.id, 'recipe', e.target.value)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => removeMenuItemRow(item.id)}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
  return (
    <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            {/* Utility Providers - At Top */}
            <Card sx={{ p: 4, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Bolt sx={{ color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight="bold">
                    Smart Utility Sync
      </Typography>
                </Box>
                <Chip label="UK ENERGY NETWORK" size="small" sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 'bold' }} />
              </Box>

              <Grid container spacing={2}>
                {['Octopus Energy', 'British Gas', 'E.ON Next', 'EDF Energy', 'Bulb'].map((provider, idx) => {
                  const selected = watch('utilityProvider') === provider;
                  return (
                    <Grid item xs={6} sm={4} md={2} key={idx}>
                      <Card
                        onClick={() => setValue('utilityProvider', provider)}
                        sx={{
                          aspectRatio: '1',
                          p: 2,
                          borderRadius: 2,
                          border: selected ? '2px solid' : '1px solid',
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? 'primary.light' : 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        {selected && (
                          <CheckCircle
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              color: 'primary.main',
                              fontSize: 20,
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 1,
                            bgcolor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                          }}
                        >
                          <Storefront sx={{ color: 'text.disabled' }} />
                        </Box>
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          textAlign="center"
                          textTransform="uppercase"
                        >
                          {provider}
                        </Typography>
                      </Card>
                    </Grid>
                  );
                })}
                <Grid item xs={6} sm={4} md={2}>
                  <Card 
              sx={{ 
                      aspectRatio: '1',
                      p: 2,
                  borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <AddIcon sx={{ color: 'text.secondary', mb: 1 }} />
                    <Typography variant="caption" fontWeight="bold" textAlign="center" textTransform="uppercase" color="text.secondary">
                      Other Provider
                    </Typography>
                  </Card>
          </Grid>
              </Grid>

              {/* Utility ingestion mode & authentication preference */}
              <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
                <Card sx={{ p: 4, borderRadius: 3, bgcolor: 'primary.main', color: 'white' }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { xs: 'flex-start', md: 'center' }, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        How should we ingest your energy data?
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Choose whether you&apos;ll connect via API, upload bills, or start manually and upgrade later.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flex: 1 }}>
                      <FormControl fullWidth required>
                        <InputLabel id="energy-ingestion-label" sx={{ backgroundColor: 'primary.main', px: 1, color: 'white' }}>
                          Energy Ingestion Mode
                        </InputLabel>
                        <Select
                          labelId="energy-ingestion-label"
                          label="Energy Ingestion Mode"
                          value={watch('energyIngestionMode') || ''}
                          {...register('energyIngestionMode', { required: 'Please choose how we should receive your energy data' })}
                          sx={{
                            borderRadius: 2,
                            bgcolor: 'white',
                            color: 'text.primary',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'transparent',
                            },
                          }}
                        >
                          <MenuItem value="api">Direct API connection</MenuItem>
                          <MenuItem value="file_upload">Upload utility bills</MenuItem>
                          <MenuItem value="manual">Manual monthly entry</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <VerifiedUser sx={{ fontSize: 18, mt: 0.5 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Read-only access to consumption data
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Security sx={{ fontSize: 18, mt: 0.5 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Encrypted transmission (AES-256)
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>
            </Card>
{/* Smart meter connector (n3rgy) */}
            <Grid container spacing={4} sx={{ mt: 4, mb: 4 }}>
              <Grid item xs={12} md={7}>
                <Card sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Bolt sx={{ color: 'primary.main', fontSize: 24, mt: 0.5 }} />
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Smart meter data via n3rgy
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        We use n3rgy as the data hub behind the scenes using RTN&apos;s API key. If you know your MPxN, you can add it now so we can request consent on your behalf.
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        label="Site MPxN (optional)"
                        placeholder="e.g. 1234567890123 from your energy bill"
                        {...register('mpxn')}
                        sx={{ mt: 1 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Move-in date (optional)"
                        InputLabelProps={{ shrink: true }}
                        {...register('mpxnMoveInDate')}
                        sx={{ mt: 2 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        If you don&apos;t know these yet, leave them blank – we&apos;ll help you connect your meter later from the dashboard.
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card sx={{ p: 4, borderRadius: 3, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'primary.50' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    What is an MPxN?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your MPxN (Meter Point Administration Number) uniquely identifies your electricity or gas supply. You&apos;ll usually find it on your energy bill or on the meter label.
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Waste Streams - 2 Column Layout */}
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <DeleteOutline sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="bold">
                      Waste Stream Configuration
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      { icon: DeleteOutline, name: 'General Waste', desc: 'Non-recyclable landfill materials', checked: true, key: 'general' },
                      { icon: '♻️', name: 'Mixed Recycling', desc: 'Paper, plastics, and metals', checked: true, key: 'recycling' },
                      { icon: '🌱', name: 'Food & Organic', desc: 'Kitchen and garden waste', checked: false, key: 'organic' },
                    ].map((stream, idx) => (
                      <Box 
                        key={stream.key}
              sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          p: 2,
                borderRadius: 2,
                          bgcolor: 'grey.50'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {typeof stream.icon === 'string' ? (
                              <Typography>{stream.icon}</Typography>
                            ) : (
                              <stream.icon sx={{ color: 'primary.main' }} />
                  )}
                </Box>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {stream.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {stream.desc}
                            </Typography>
                          </Box>
                        </Box>
                        <Switch defaultChecked={stream.checked} />
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    Waste Tracking Benefits
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <CheckCircle sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Automated reporting
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Track waste streams automatically for accurate carbon calculations
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <CheckCircle sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Cost optimization
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Identify opportunities to reduce waste and disposal costs
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <CheckCircle sx={{ color: 'primary.main', mt: 0.5 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Compliance ready
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Generate reports for environmental compliance and certifications
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>
        </Grid>
            </Grid>

            
    </Box>
  );

      case 3:
        return (
          <Box sx={{ animation: 'slideIn 0.5s ease' }}>
            <Grid container spacing={4}>
              {/* Left Column - Events */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CalendarToday sx={{ color: 'primary.main', fontSize: 28 }} />
                      <Typography variant="h5" fontWeight="bold">
                        Events & Exceptions
            </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      size="small"
                      sx={{ borderRadius: 2, fontWeight: 'bold' }}
                      onClick={addEvent}
                    >
                      Add Event
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Add local events that might affect your operations and footfall.
            </Typography>
                </Box>

                <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 4 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Event Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Frequency</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Impact</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {event.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {event.frequency === 'Weekly' ? 'Recurring every Saturday' : 'August 15th - 18th'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={event.frequency} 
                              size="small" 
                              sx={{ bgcolor: 'grey.100', fontWeight: 'bold', textTransform: 'uppercase' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={event.impact}
                              size="small"
                              sx={{ 
                                bgcolor: event.impact === 'Extreme' ? 'orange.100' : 'green.100',
                                color: event.impact === 'Extreme' ? 'orange.700' : 'green.700',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>

                {/* Final CTA - Moved here for better flow */}
                <Card 
                  sx={{ 
                    p: 4, 
                    borderRadius: 3, 
                    background: 'linear-gradient(135deg, #144bb8 0%, #0d47a1 100%)',
                    color: 'white',
                    textAlign: 'center'
                  }}
                >
                  <Box sx={{ 
                    width: 56, 
                    height: 56, 
                    borderRadius: '50%', 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    <RocketLaunch sx={{ fontSize: 32 }} />
          </Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Ready to unlock your insights?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                    Complete your setup to trigger our neural engine.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<AutoAwesome />}
                    sx={{ 
                      bgcolor: 'white', 
                      color: 'primary.main',
                      fontWeight: 'bold',
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'grey.100', transform: 'scale(1.02)' }
                    }}
                    onClick={handleNext}
                  >
                    Complete & Generate Intelligence
                  </Button>
                </Card>
              </Grid>

              {/* Right Column - Social Media */}
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Hub sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="bold">
                      Social Media Hub
</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Connect your channels to correlate marketing campaigns with footfall spikes.
            </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {[
                    { name: 'Instagram', desc: 'Analyze visual engagement and peak engagement hours for local demographics.', recommended: true, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 50%, #4facfe 100%)' },
                    { name: 'Facebook Business', desc: 'Track community events and high-intent local user interactions.', status: 'Disconnected' },
                    { name: 'TikTok Shop', desc: 'Sync viral local trends and short-form content performance data.', status: 'Optional', outline: true },
                  ].map((platform, idx) => (
                    <Card 
                      key={idx}
                      sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ 
                          width: 48, 
                          height: 48, 
                          borderRadius: 2,
                          background: platform.gradient || '#1877F2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}>
                          <Hub />
                        </Box>
                        {platform.recommended && (
                          <Chip 
                            label="Recommended" 
                          size="small"
                            sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 'bold', fontSize: '0.65rem' }}
                          />
                        )}
                        {platform.status && (
                          <Chip 
                            label={platform.status} 
                          size="small"
                            sx={{ bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 'bold', fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {platform.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {platform.desc}
                      </Typography>
                      <Button
                        variant={platform.outline ? 'outlined' : 'contained'}
                        fullWidth
                        sx={{ 
                          borderRadius: 2, 
                          fontWeight: 'bold',
                          py: 1.25
                        }}
                      >
                        Connect {platform.name.split(' ')[0]}
                      </Button>
                  </Card>
              ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const completeOnboarding = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';

      const restaurantName = watch('restaurantName');
      const postcode = watch('postcode');
      const address = watch('address');
      const serviceType = watch('serviceType');
      const floorAreaM2 = watch('floorAreaM2');
      const utilityProvider = watch('utilityProvider');
      const energyIngestionMode = watch('energyIngestionMode') || 'manual_upload';
      const mpxn = watch('mpxn');
      const mpxnMoveInDate = watch('mpxnMoveInDate');

      const sitePayload = {
        name: restaurantName,
        postcode,
        fullAddress: address,
        floorAreaM2: floorAreaM2 ? Number(floorAreaM2) : null,
        siteType: serviceType || '',
        serviceTypes: serviceType ? [serviceType] : [],
      };

      const settingsPayload = {
        pos_ingestion_mode: connectedPOS ? 'api' : 'manual',
        energy_ingestion_mode: energyIngestionMode,
        forecast_horizon_days: 7,
        baseline_start_month: null,
        min_staff_count: staffMembers.length || null,
        employee_data: { staff: staffMembers },
        stock_key_list: [],
        config_json: {
          utilityProvider: utilityProvider || null,
          menu_items: menuItems, // hold onboarding menu until deeper schema is wired
          mpxn: mpxn || null,
          mpxnMoveInDate: mpxnMoveInDate || null,
        },
        opening_hours: operatingHours,
        service_type: serviceType ? [serviceType] : [],
        own_hvac_mode: null,
      };

      const integrationsPayload = [];
      if (connectedPOS) {
        const posIntegration = {
          category: 'POS',
          name: connectedPOS,
          auth_type: 'oauth',
          status: 'connected',
        };

        // If we have live OAuth tokens from Square, attach them so they are
        // persisted when the site/integration records are created.
        if (posTokens) {
          posIntegration.access_token = posTokens.access_token || null;
          posIntegration.refresh_token = posTokens.refresh_token || null;
          posIntegration.token_expires_at = posTokens.expires_at || null;
          posIntegration.token_scope = posTokens.scope || null;
          posIntegration.config_json = {
            merchant_id: posTokens.merchant_id || null,
            application_id: posTokens.application_id || null,
            pos_vendor: 'Square',
          };
        }

        integrationsPayload.push(posIntegration);
      }

      const payload = {
        organisationId: user?.organisationId,
        site: sitePayload,
        settings: settingsPayload,
        integrations: integrationsPayload,
      };

      const res = await fetch(`${baseUrl}/api/sites/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok || !body.success) {
        throw new Error(body.message || 'Failed to complete onboarding.');
      }

      if (body.site?._id) {
        setCurrentSiteId(body.site._id);
      }

      navigate('/dashboard');
    } catch (err) {
      console.error('Onboarding error:', err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    await handleNext();
  };

  const progress = ((activeStep + 1) / steps.length) * 100;

  return (
    <>
      <Backdrop
        open={loading && activeStep === steps.length - 1}
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'rgba(20, 75, 184, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ 
            width: 192, 
            height: 192, 
            borderRadius: '50%', 
            border: '4px solid',
            borderColor: 'primary.main',
            borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
            position: 'relative',
            mb: 4,
            mx: 'auto'
          }}>
            <Box sx={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 60,
              color: 'primary.main'
            }}>
              <AutoAwesome />
            </Box>
          </Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Crafting Your AI Models...
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', maxWidth: 300, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'success.main' }}>
              <CheckCircle sx={{ fontSize: 18 }} />
              <Typography variant="body2">Analyzing 12 months of local trends...</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'info.main' }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Syncing social demographics...</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.disabled' }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Calibrating footfall heatmaps...</Typography>
            </Box>
          </Box>
        </Box>
      </Backdrop>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header */}
        <Paper 
          elevation={0}
          sx={{ 
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              py: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  bgcolor: 'primary.main',
                  borderRadius: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  RTN
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  RTNZero.AI
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {steps.map((step, index) => (
                  <Box 
                    key={index}
              sx={{
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      pb: 1,
                      borderBottom: activeStep === index ? '2px solid' : '2px solid transparent',
                      borderColor: activeStep === index ? 'primary.main' : 'transparent'
                    }}
                  >
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%',
                      bgcolor: activeStep === index ? 'primary.main' : 'grey.200',
                      color: activeStep === index ? 'white' : 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {index + 1}
                    </Box>
                    <Typography 
                      variant="body2" 
                      fontWeight={activeStep === index ? 'bold' : 'medium'}
                      color={activeStep === index ? 'primary.main' : 'text.secondary'}
                      sx={{ display: { xs: 'none', md: 'block' } }}
                    >
                      {step.title}
            </Typography>
                  </Box>
                ))}
                <Button 
                  variant="outlined" 
                  size="small"
                  sx={{ borderRadius: 2, fontWeight: 'bold' }}
                >
                  Help
                </Button>
              </Box>
            </Box>
          </Container>
        </Paper>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          {/* Progress Indicator */}
          <Card sx={{ p: 3, mb: 6, borderRadius: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">
                {activeStep === steps.length - 1 
                  ? 'Final Step: Refining Your AI Intelligence'
                  : `Step ${activeStep + 1} of ${steps.length}: ${steps[activeStep].title}`
                }
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="primary.main">
                {Math.round(progress)}%
              </Typography>
            </Box>
              <Box sx={{ 
                width: '100%', 
              height: 10, 
              bgcolor: 'grey.200', 
              borderRadius: 5,
                overflow: 'hidden'
              }}>
                <Box sx={{
                width: `${progress}%`, 
                  height: '100%',
                bgcolor: 'primary.main',
                borderRadius: 5,
                transition: 'width 0.5s ease'
                }} />
              </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
              Onboarding Progress
            </Typography>
          </Card>

          {/* Page Heading */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h3" fontWeight="bold">
                {steps[activeStep].title}
              </Typography>
              {activeStep === 3 && (
                <Button
                  variant="outlined"
                  startIcon={<PlayCircle />}
                  sx={{ borderRadius: 2, fontWeight: 'bold' }}
                >
                  View Tutorial
                </Button>
              )}
            </Box>
          </Box>

          {/* Form Content */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mb: 6, minHeight: 400 }}>
              {renderStepContent(activeStep)}
            </Box>

            {/* Navigation Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              pt: 4,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Button
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                variant="outlined"
                startIcon={<ArrowBack />}
                sx={{ 
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 'bold',
                  display: activeStep === 0 ? 'none' : 'flex'
                }}
              >
                Back
              </Button>
              
              <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                {activeStep < steps.length - 1 && (
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}
                  >
                    Skip for now
                  </Button>
                )}
              <Button
                type="submit"
                variant="contained"
                  endIcon={activeStep === steps.length - 1 ? <AutoAwesome /> : <ArrowForward />}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 4,
                  py: 1.2,
                    fontWeight: 'bold',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                    'Complete & Generate Intelligence'
                ) : (
                    'Continue'
                )}
              </Button>
              </Box>
            </Box>
          </form>
      </Container>
      </Box>

      <style>
        {`
          @keyframes slideIn {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default Onboarding;
